import React, { useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import RangeInput, { RangeType } from "../../components/Inputs/RangeInput";
import FilterModal from "../../components/Modal/FilterModal";
import { useModal } from "../../components/Modal/useModal";
import SearchBox from "../../components/SearchBox/SearchBox";
import { clearAdvancedFilters as clearAction, updateFilters as filterAction } from "../../data/hcpLandscape/actions";
import { useHcpLandscapeDispatch, useHcpLandscapeState } from "../../data/hcpLandscape/hooks";
import hcpLandscapeReducer from "../../data/hcpLandscape/reducer";
import useGetRole, { FRM_ROLE } from "../../hooks/useGetRole";
import { nullToUndefined } from "../../utils/typeUtils";
import { useHcpLandscapeFilterVisibility } from "./useHCPLandscapeColumnVisibility";

const DetailFiltersModal: React.FC<{ client: string }> = ({ client }) => {
  const { t, i18n } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);
  const { setModal } = useModal();
  const hcpLandscapeState = useHcpLandscapeState();
  const { updateFilters, clearAdvancedFilters } = useHcpLandscapeDispatch();
  const [{ filters }, dispatch] = useReducer(hcpLandscapeReducer, hcpLandscapeState);
  const { data: role } = useGetRole(client);
  const isFrm = String(role) === FRM_ROLE;

  const timePeriod = hcpLandscapeState.filters.timePeriod;
  const indication = hcpLandscapeState.filters.indication;

  const client_indicationkey = client === "BRAFTOVI" ? client + "_" + indication?.replaceAll(" ", "") : client;

  // Use centralized filter visibility logic
  const { isFilterVisible } = useHcpLandscapeFilterVisibility(client, indication, isFrm);

  const clear = () => {
    dispatch(clearAction());
    clearAdvancedFilters();
    setRefreshKey((key) => key + 1);
  };

  const filter = () => {
    updateFilters(filters);
    setModal(undefined);
  };

  return (
    <FilterModal
      onClear={clear}
      onFilter={filter}
      title={t([`hcpLandscape.hcpLandscapeFilters.${client}`, `hcpLandscape.hcpLandscapeFilters.default`])}
    >
      {/* Always visible filters */}
      <SearchBox
        value={filters.name}
        handleChange={(value) => dispatch(filterAction({ name: value }))}
        label={t([`hcpLandscape.hcps.${client}`, `hcpLandscape.hcps.default`])}
      />

      {/* Conditionally visible filters based on columns */}
      {isFilterVisible("favorableAccess") && (
        <RangeInput
          key={`favorableAccessPercent-${refreshKey}`}
          range={{
            min: filters.favorableAccessPercentMin,
            max: filters.favorableAccessPercentMax,
            rangeType: filters.favorableAccessPercentRangeType || RangeType.GE,
          }}
          setRange={(range) =>
            dispatch(
              filterAction({
                favorableAccessPercentMin: nullToUndefined(range.min),
                favorableAccessPercentMax: nullToUndefined(range.max),
                favorableAccessPercentRangeType: filters.favorableAccessPercentRangeType || RangeType.GE,
              }),
            )
          }
          label={
            i18n.exists(`hcpLandscape.favorableAccess.${client_indicationkey}`)
              ? t(`hcpLandscape.favorableAccess.${client_indicationkey}`)
              : t(`hcpLandscape.favorableAccess.default`)
          }
          allowNegative={false}
        />
      )}

      {isFilterVisible("favorableAccessChange") && (
        <RangeInput
          key={`favorableAccessChangePercent-${refreshKey}`}
          range={{
            min: filters.favorableAccessChangePercentMin,
            max: filters.favorableAccessChangePercentMax,
            rangeType: filters.favorableAccessChangePercentRangeType || RangeType.GE,
          }}
          setRange={(range) =>
            dispatch(
              filterAction({
                favorableAccessChangePercentMin: nullToUndefined(range.min),
                favorableAccessChangePercentMax: nullToUndefined(range.max),
                favorableAccessChangePercentRangeType: filters.favorableAccessChangePercentRangeType || RangeType.GE,
              }),
            )
          }
          label={
            i18n.exists(`hcpLandscape.changeInFa.${client}`)
              ? t(`hcpLandscape.changeInFa.${client}`, { channel: filters.benificiaryType })
              : t(`hcpLandscape.changeInFa.default`)
          }
          allowNegative={false}
        />
      )}

      {isFilterVisible("competitorProductTrx") && (
        <RangeInput
          key={`compProdTrx-${refreshKey}`}
          range={{
            min: filters.compTrxMin,
            max: filters.compTrxMax,
            rangeType: filters.compTrxRangeType || RangeType.GE,
          }}
          setRange={(range) =>
            dispatch(
              filterAction({
                compTrxMin: nullToUndefined(range.min),
                compTrxMax: nullToUndefined(range.max),
                compTrxRangeType: filters.compTrxRangeType || RangeType.GE,
              }),
            )
          }
          label={
            i18n.exists(`hcpLandscape.compProdTrx.${timePeriod}.${client}`)
              ? t(`hcpLandscape.compProdTrx.${timePeriod}.${client}`, { channel: filters.benificiaryType })
              : t(`hcpLandscape.compProdTrx.default`)
          }
          allowNegative={false}
        />
      )}

      {isFilterVisible("overallAccess") && (
        <RangeInput
          key={`overallAccessPercent-${refreshKey}`}
          range={{
            min: filters.overallAccessPercentMin,
            max: filters.overallAccessPercentMax,
            rangeType: filters.overallAccessPercentRangeType || RangeType.GE,
          }}
          setRange={(range) =>
            dispatch(
              filterAction({
                overallAccessPercentMin: nullToUndefined(range.min),
                overallAccessPercentMax: nullToUndefined(range.max),
                overallAccessPercentRangeType: filters.overallAccessPercentRangeType || RangeType.GE,
              }),
            )
          }
          label={
            i18n.exists(`hcpLandscape.overallAccess.${client}`)
              ? t(`hcpLandscape.overallAccess.${client}`)
              : t(`hcpLandscape.overallAccess.default`)
          }
          allowNegative={false}
        />
      )}

      {isFilterVisible("marketTrx") && (
        <RangeInput
          key={`marketTrx-${refreshKey}`}
          range={{
            min: filters.marketTrxMin,
            max: filters.marketTrxMax,
            rangeType: filters.marketRangeType!,
          }}
          setRange={(range) =>
            dispatch(
              filterAction({
                marketTrxMin: nullToUndefined(range.min),
                marketTrxMax: nullToUndefined(range.max),
                marketRangeType: filters.marketRangeType || RangeType.GE,
              }),
            )
          }
          label={
            i18n.exists(`hcpLandscape.marketTrx.${filters.timePeriod}.${client}`)
              ? t(`hcpLandscape.marketTrx.${filters.timePeriod}.${client}`)
              : t(`hcpLandscape.marketTrx.${filters.timePeriod}.default`)
          }
          allowNegative={false}
        />
      )}

      {isFilterVisible("productTrx") && (
        <RangeInput
          key={`cibinqoTrx-${refreshKey}`}
          range={{
            min: filters.trxMin,
            max: filters.trxMax,
            rangeType: filters.trxRangeType!,
          }}
          setRange={(range) =>
            dispatch(
              filterAction({
                trxMin: nullToUndefined(range.min),
                trxMax: nullToUndefined(range.max),
                trxRangeType: filters.trxRangeType || RangeType.GE,
              }),
            )
          }
          label={
            i18n.exists(`hcpLandscape.productTrx.${filters.timePeriod}.${client}`, { product: client })
              ? t(`hcpLandscape.productTrx.${filters.timePeriod}.${client}`, { product: client })
              : t(`hcpLandscape.productTrx.${filters.timePeriod}.default`, { product: client })
          }
          allowNegative={false}
        />
      )}

      {isFilterVisible("productShare") && (
        <RangeInput
          key={`productShare-${refreshKey}`}
          range={{
            min: filters.productShareMin,
            max: filters.productShareMax,
            rangeType: filters.productShareRangeType!,
          }}
          setRange={(range) =>
            dispatch(
              filterAction({
                productShareMin: nullToUndefined(range.min),
                productShareMax: nullToUndefined(range.max),
                productShareRangeType: filters.productShareRangeType || RangeType.GE,
              }),
            )
          }
          label={
            i18n.exists(`hcpLandscape.productShare.${filters.timePeriod}.${client}`, { product: client })
              ? t(`hcpLandscape.productShare.${filters.timePeriod}.${client}`, { product: client })
              : t(`hcpLandscape.productShare.${filters.timePeriod}.default`)
          }
          allowNegative={false}
        />
      )}
    </FilterModal>
  );
};

export default DetailFiltersModal;
